import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MentionUsersPage } from "@/lib/humhub-api/mentionUsers";
import { studyGroupMembershipApi } from "@/lib/humhub-api/studyGroupMembership";
import type { User } from "@/lib/humhub-api/types";
import { studyGroupKeys } from "@/lib/queryKeys/studyGroupKeys";

const mergeUsers = (current: User[], incoming: User[]) => {
  const users = new Map<string, User>();
  for (const user of [...current, ...incoming]) {
    users.set(String(user.guid ?? user.id), user);
  }
  return [...users.values()];
};

const getUserKey = (user: User) => String(user.guid ?? user.id);

interface FirstPageSnapshot {
  userIds: Set<string>;
  total: number;
  pages: number;
}

const createFirstPageSnapshot = (
  page: MentionUsersPage,
): FirstPageSnapshot => ({
  userIds: new Set(page.users.map(getUserKey)),
  total: page.total,
  pages: page.pages,
});

const didFirstPageChange = (
  previous: FirstPageSnapshot,
  next: FirstPageSnapshot,
) =>
  previous.total !== next.total ||
  previous.pages !== next.pages ||
  previous.userIds.size !== next.userIds.size ||
  [...next.userIds].some((userId) => !previous.userIds.has(userId));

export const useStudyGroupMentionAvailableUsers = (
  studyGroupId: string | number | null,
  options: { topicIds?: Array<string | number>; limit?: number } = {},
) => {
  const limit = options.limit ?? 10;
  const [keyword, setKeyword] = useState<string | undefined>();
  const [users, setUsers] = useState<User[]>([]);
  const [pageInfo, setPageInfo] = useState<MentionUsersPage | null>(null);
  const loadedThroughPageRef = useRef(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const latestUsersRef = useRef<User[]>([]);
  const firstPageSnapshotRef = useRef<FirstPageSnapshot | null>(null);
  const queryClient = useQueryClient();
  const topicIds = useMemo(
    () =>
      options.topicIds
        ?.map(Number)
        .filter((id) => Number.isFinite(id) && id > 0)
        .map(String),
    [options.topicIds],
  );
  const topicKey = topicIds?.join(",") ?? "";
  const mentionScopeKey = [studyGroupId ?? "", topicKey].join(":");

  const fetchPage = useCallback(
    (searchKeyword: string | undefined, page: number) => {
      if (!studyGroupId) {
        return Promise.resolve<MentionUsersPage>({
          users: [],
          total: 0,
          page: 1,
          pages: 0,
          hasMore: false,
        });
      }
      return queryClient.fetchQuery({
        queryKey: studyGroupKeys.mentionAvailableUsers(
          studyGroupId,
          searchKeyword,
          topicIds,
          limit,
          page,
        ),
        queryFn: () =>
          studyGroupMembershipApi.getMentionAvailableUsers(studyGroupId, {
            keyword: searchKeyword,
            topicIds,
            limit,
            page,
          }),
        staleTime: 3 * 60 * 1000,
      });
    },
    [limit, queryClient, studyGroupId, topicIds],
  );

  useEffect(() => {
    void mentionScopeKey;
    void limit;
    requestIdRef.current += 1;
    setKeyword(undefined);
    latestUsersRef.current = [];
    setUsers([]);
    firstPageSnapshotRef.current = null;
    setPageInfo(null);
    loadedThroughPageRef.current = 0;
    setIsFetchingMore(false);
    setLoadMoreError(null);
  }, [mentionScopeKey, limit]);

  const { data, isLoading, error } = useQuery({
    queryKey: studyGroupKeys.mentionAvailableUsers(
      studyGroupId,
      keyword,
      topicIds,
      limit,
      1,
    ),
    queryFn: () =>
      studyGroupId
        ? studyGroupMembershipApi.getMentionAvailableUsers(studyGroupId, {
            keyword,
            topicIds,
            limit,
            page: 1,
          })
        : Promise.resolve<MentionUsersPage>({
            users: [],
            total: 0,
            page: 1,
            pages: 0,
            hasMore: false,
          }),
    enabled: Boolean(studyGroupId),
    staleTime: 3 * 60 * 1000,
  });

  useEffect(() => {
    if (!data) return;
    const nextFirstPageSnapshot = createFirstPageSnapshot(data);
    if (loadedThroughPageRef.current > 1) {
      const previousFirstPageSnapshot = firstPageSnapshotRef.current;
      if (
        previousFirstPageSnapshot &&
        didFirstPageChange(previousFirstPageSnapshot, nextFirstPageSnapshot)
      ) {
        requestIdRef.current += 1;
        setIsFetchingMore(false);
        const pageOneKey = studyGroupKeys.mentionAvailableUsers(
          studyGroupId,
          keyword,
          topicIds,
          limit,
          1,
        );
        queryClient.removeQueries({
          predicate: ({ queryKey }) =>
            pageOneKey
              .slice(0, -1)
              .every((part, index) => Object.is(part, queryKey[index])) &&
            Number(queryKey.at(-1)) > 1,
        });
        latestUsersRef.current = data.users;
        setUsers(data.users);
        firstPageSnapshotRef.current = nextFirstPageSnapshot;
        setPageInfo(data);
        loadedThroughPageRef.current = 1;
        setLoadMoreError(null);
        return;
      }
      setUsers((currentUsers) => {
        const appendedUsers = currentUsers.filter(
          (user) => !previousFirstPageSnapshot?.userIds.has(getUserKey(user)),
        );
        const nextUsers = mergeUsers(data.users, appendedUsers);
        latestUsersRef.current = nextUsers;
        return nextUsers;
      });
      firstPageSnapshotRef.current = nextFirstPageSnapshot;
      setPageInfo((currentPage) =>
        currentPage
          ? {
              ...currentPage,
              total: data.total,
              pages: data.pages,
              hasMore: currentPage.page < data.pages,
            }
          : data,
      );
      setLoadMoreError(null);
      return;
    }
    latestUsersRef.current = data.users;
    setUsers(data.users);
    firstPageSnapshotRef.current = nextFirstPageSnapshot;
    setPageInfo(data);
    loadedThroughPageRef.current = 1;
    setLoadMoreError(null);
  }, [data, keyword, limit, queryClient, studyGroupId, topicIds]);

  const searchMentionAvailableUsers = useCallback(
    async (newKeyword?: string) => {
      const requestId = ++requestIdRef.current;
      setKeyword(newKeyword);
      latestUsersRef.current = [];
      setUsers([]);
      firstPageSnapshotRef.current = null;
      setPageInfo(null);
      loadedThroughPageRef.current = 0;
      setIsFetchingMore(false);
      setLoadMoreError(null);
      const result = await fetchPage(newKeyword, 1);
      if (requestId !== requestIdRef.current) return [];
      latestUsersRef.current = result.users;
      setUsers(result.users);
      firstPageSnapshotRef.current = createFirstPageSnapshot(result);
      setPageInfo(result);
      loadedThroughPageRef.current = 1;
      setLoadMoreError(null);
      return result.users;
    },
    [fetchPage],
  );

  const fetchNextMentionUsers = useCallback(async () => {
    const currentPage = pageInfo ?? data ?? null;
    const currentUsers = users.length > 0 ? users : (data?.users ?? []);
    if (!currentPage?.hasMore || isFetchingMore) return currentUsers;
    const requestId = requestIdRef.current;
    setIsFetchingMore(true);
    setLoadMoreError(null);
    try {
      const result = await fetchPage(keyword, currentPage.page + 1);
      if (requestId !== requestIdRef.current) return latestUsersRef.current;
      const nextUsers = mergeUsers(currentUsers, result.users);
      latestUsersRef.current = nextUsers;
      setUsers(nextUsers);
      setPageInfo(result);
      loadedThroughPageRef.current = result.page;
      return nextUsers;
    } catch (fetchError) {
      if (requestId === requestIdRef.current) {
        setLoadMoreError(String(fetchError));
      }
      return requestId === requestIdRef.current
        ? currentUsers
        : latestUsersRef.current;
    } finally {
      if (requestId === requestIdRef.current) {
        setIsFetchingMore(false);
      }
    }
  }, [data, fetchPage, isFetchingMore, keyword, pageInfo, users]);

  return {
    data: users.length > 0 ? users : (data?.users ?? []),
    pagination: pageInfo,
    hasMore: pageInfo?.hasMore ?? data?.hasMore ?? false,
    isLoading,
    isFetchingMore,
    loadMoreError,
    error: error ? String(error) : null,
    fetchPage: (params?: { keyword?: string }) =>
      searchMentionAvailableUsers(params?.keyword),
    searchMentionAvailableUsers,
    fetchNextMentionUsers,
    mentionScopeKey,
  };
};
